// Modules
import React, { useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { ErrorResponse } from 'apollo-link-error'

// Applications
import { useApp } from 'context'
import { useActiveCIPOrphanHook, useAnalytics, useLoginHook } from 'hooks'
import { ContactType, StatusMessage } from 'typings'
import { OutputErrorCode, OutputErrorMsg, Analytics, ROUTES_PATH } from 'constants/index'

import { ResendTokenType } from 'typings/account'
// Components
import { ValidateTokenInitial } from 'components/Presenters/ValidateDestinationToken'
import { ValidateToken } from 'components/Presenters/ValidateToken'
import { TemplateContainer, Header } from 'components'

export const NEWCIPValidateToken: React.VFC = () => {
    const history = useHistory()
    const { openToast } = useApp()
    const { verifyUser, signOut } = useLoginHook()
    const {
        cpf,
        biologicalSex,
        phone,
        password,
        hasMounted,
        isSubmitting,
        name,
        birthDate,
        confirmToken,
        resendToken,
        resetAccountState,
    } = useActiveCIPOrphanHook()

    const [isLoading, setLoading] = useState<boolean>(false)
    const [fieldState, setFieldState] = useState<StatusMessage>({ status: '', message: '' })

    const shouldRedirect = !cpf || !biologicalSex || !phone || !password || !name || !birthDate

    useAnalytics(Analytics.ActivateAccountCipOrphan.NewValidateToken, !shouldRedirect)

    const SUBTITLE_CONTENT: string = useMemo(() => {
        let subtitle = 'Um código de segurança foi enviado para o [CONTACT_TYPE] <span>[CONTACT_VALUE]</span>.'
        subtitle = subtitle.replace('[CONTACT_VALUE]', phone || '')
        subtitle = subtitle.replace('[CONTACT_TYPE]', 'celular')

        return subtitle
    }, [phone])

    if (!hasMounted) {
        return null
    }

    if (shouldRedirect) {
        history.push(ROUTES_PATH.SIGN_UP_REGISTER_ACTIVATION_CPF)
        return null
    }

    const handleSubmit = async (values: ValidateTokenInitial) => {
        setFieldState({ status: 'loading', message: 'Validando código' })
        const { token: rawToken } = values
        const token = rawToken.join('')

        sessionStorage.setItem('@LiviaAuth:PhoneToken', token)

        try {
            const successful = await confirmToken(token)
            setFieldState({ status: 'success', message: 'Código validado' })

            if (successful) {
                const { isRedirect } = await verifyUser()

                if (!isRedirect) {
                    signOut()
                    resetAccountState()
                    history.push(ROUTES_PATH.SIGN_IN, {
                        analytics: Analytics.ActivateAccountCipOrphan.RegisterExamesNewSignUp,
                    })
                }
            } else {
                setTimeout(() => {
                    history.push(ROUTES_PATH.SIGN_IN, { status: 'success', message: 'Cadastro realizado com sucesso!' })
                }, 3600)
            }
        } catch (err) {
            const e: ErrorResponse = err
            if (e.graphQLErrors && e.graphQLErrors.length > 0) {
                const codeError = e.graphQLErrors[0].extensions?.code
                if (codeError === OutputErrorCode.INVALIDTOKEN || codeError === OutputErrorCode.EXPIREDTOKEN) {
                    setFieldState({
                        status: 'error',
                        message:
                            OutputErrorCode.INVALIDTOKEN === codeError ? OutputErrorMsg.INVALIDTOKEN : OutputErrorMsg.EXPIREDTOKEN,
                    })
                    return
                }
            }

            setFieldState({ status: '', message: '' })
            openToast('Ocorreu um erro')
        }
    }

    const handleResendToken = async () => {
        try {
            setLoading(true)
            const patientLogin: ResendTokenType = {
                cellphone: phone,
                cpf: cpf,
                password: password,
                destination: ContactType.SMS,
            }
            await resendToken(patientLogin)
            setLoading(false)
        } catch (error) {
            setLoading(false)
            openToast('Ocorreu um erro')
        }
    }

    return (
        <TemplateContainer>
            <Header subtitle={SUBTITLE_CONTENT} title="Insira o código" />
            <ValidateToken
                fieldState={fieldState}
                handleResendToken={handleResendToken}
                handleSubmit={handleSubmit}
                initialValues={{
                    token: [null, null, null, null, null, null],
                }}
                isDestinationMobile={true}
                isLoading={isLoading}
                isSubmitting={isSubmitting}
            />
        </TemplateContainer>
    )
}
